import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AdminContext = createContext(null);

export const resolveSrc = (src) =>
  src?.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;

export const AdminProvider = ({ children }) => {
  const [ads, setAds] = useState([]);
  const [socials, setSocials] = useState([]);
  const [passcode, setPasscode] = useState(() => localStorage.getItem("onyqx_admin") || "");

  const isAdmin = Boolean(passcode);

  const refreshAds = useCallback(async () => {
    const { data } = await axios.get(`${API}/ads`);
    setAds(data);
  }, []);

  const refreshSocials = useCallback(async () => {
    const { data } = await axios.get(`${API}/socials`);
    setSocials(data);
  }, []);

  useEffect(() => {
    refreshAds();
    refreshSocials();
  }, [refreshAds, refreshSocials]);

  const login = async (code) => {
    const { data } = await axios.post(`${API}/admin/verify`, { passcode: code });
    if (data.valid) {
      localStorage.setItem("onyqx_admin", code);
      setPasscode(code);
    }
    return data.valid;
  };

  const logout = () => {
    localStorage.removeItem("onyqx_admin");
    setPasscode("");
  };

  const uploadAd = async ({ title, subtitle, file, poster, onProgress }) => {
    const fd = new FormData();
    fd.append("title", title);
    if (subtitle) fd.append("subtitle", subtitle);
    fd.append("file", file);
    if (poster) fd.append("poster", poster);
    await axios.post(`${API}/ads`, fd, {
      headers: { "X-Admin-Passcode": passcode },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
    });
    await refreshAds();
  };

  const setAdThumbnail = async (id, poster) => {
    const fd = new FormData();
    fd.append("poster", poster);
    await axios.put(`${API}/ads/${id}/poster`, fd, { headers: { "X-Admin-Passcode": passcode } });
    await refreshAds();
  };

  const deleteAd = async (id) => {
    await axios.delete(`${API}/ads/${id}`, { headers: { "X-Admin-Passcode": passcode } });
    await refreshAds();
  };

  const reorderAds = async (ids) => {
    await axios.post(`${API}/ads/reorder`, { order: ids }, { headers: { "X-Admin-Passcode": passcode } });
    await refreshAds();
  };

  const updateSocials = async (links) => {
    const { data } = await axios.put(
      `${API}/socials`, { socials: links }, { headers: { "X-Admin-Passcode": passcode } }
    );
    setSocials(data.socials);
  };

  return (
    <AdminContext.Provider
      value={{ ads, socials, refreshAds, isAdmin, login, logout, uploadAd, deleteAd, reorderAds, updateSocials, setAdThumbnail }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);
